import { Button } from "@/components/ui/button";
import { ROUTES } from "@/constants/routes";
import { useMainRouteParams } from "@/hooks/pane-routing";
import { route } from "@/utils/route";
import { Entity, IntegrationIcon } from "@cohesive/ui-components";
import { useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { EntitiesDialog } from "./Dialogs";
import { useAppNavigate, usePaneRouterContextSafe } from "./PaneRouter";

interface SourcesProps {
  entities?: Map<string, Entity>;
  flowSessionId: string;
  flowMessageId: string;
}

export function Sources({
  entities = new Map<string, Entity>(),
  flowSessionId,
  flowMessageId,
}: SourcesProps) {
  const appNavigate = useAppNavigate();
  const navigate = useNavigate();
  const location = useLocation();
  const [sourcesDialogOpen, setSourcesDialogOpen] = useState(false);
  const hasEntities = entities && entities.size > 0;

  const paneRouter = usePaneRouterContextSafe();
  const { canvasId } = useMainRouteParams();

  // Calculate total count and unique platforms for display
  const totalCount = hasEntities ? entities.size : 0;
  const uniquePlatforms = Array.from(
    new Set(Array.from(entities.values()).map((e) => e.platform) ?? []),
  ).slice(0, 3);

  if (!hasEntities) {
    return <div />;
  }

  const handleViewSources = () => {
    if (paneRouter && !canvasId) {
      navigate(
        route(ROUTES.SESSIONS.SESSION, {
          sessionId: flowSessionId,
        }),
        {
          state: {
            ...location.state,
            rightPane: {
              pathname: route(ROUTES.ENTITIES.LIST, {
                flowSessionId,
                flowMessageId,
              }),
            },
          },
        },
      );
      return;
    }
    appNavigate({
      pane: "rightPane",
      to: route(ROUTES.ENTITIES.LIST, {
        flowSessionId,
        flowMessageId,
      }),
    });
  };

  return (
    <>
      <div className="mt-2 pb-2">
        <Button
          variant="outline"
          size="sm"
          className="inline-flex items-center rounded-full border px-3 py-1 text-[12px] h-7 gap-2"
          onClick={handleViewSources}
        >
          <div className="flex items-center -space-x-1">
            {/* Show entity platform icons if entities exist */}
            {hasEntities &&
              uniquePlatforms.map((platform, idx) => (
                <div
                  key={idx}
                  className="relative inline-flex items-center justify-center"
                  style={{ width: 20, height: 20 }}
                >
                  <IntegrationIcon platform={platform} className="w-5 h-5" />
                </div>
              ))}
          </div>

          <span className="font-medium">Sources</span>
          <span className="text-muted-foreground">{totalCount}</span>
        </Button>
      </div>
      <EntitiesDialog
        open={sourcesDialogOpen}
        onOpenChange={setSourcesDialogOpen}
        flowSessionId={flowSessionId}
        flowMessageId={flowMessageId}
      />
    </>
  );
}

export default Sources;
